import React, { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { usePlacesAutocomplete } from "../../../lib/googleMaps";

/**
 * QuickBookCard — compact inline form shown between the hero and trust bar for
 * logged-in clients. On submit, hands over pre-filled data to the BookingModal
 * (client name/email/phone + pickup/destination/datetime/category) so the client
 * only has to review and confirm.
 */
export default function QuickBookCard({ fleet, clientPrefill, userName, onSubmit }) {
  const { t } = useLang();
  const [pickup, setPickup] = useState({ address: "" });
  const [dest, setDest] = useState({ address: "" });
  const [dt, setDt] = useState("");
  const [category, setCategory] = useState("executive");
  const pickupRef = useRef(null);
  const destRef = useRef(null);

  usePlacesAutocomplete(pickupRef, (place) =>
    setPickup({ address: place.formatted_address, place_id: place.place_id, lat: place.lat, lng: place.lng })
  );
  usePlacesAutocomplete(destRef, (place) =>
    setDest({ address: place.formatted_address, place_id: place.place_id, lat: place.lat, lng: place.lng })
  );

  const minDateTime = new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 16);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(
      {
        ...clientPrefill,
        pickup_address: pickup.address,
        pickup_place_id: pickup.place_id,
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        destination_address: dest.address,
        destination_place_id: dest.place_id,
        destination_lat: dest.lat,
        destination_lng: dest.lng,
        service_datetime: dt,
      },
      category
    );
  };

  return (
    <section className="dt-quickbook" data-testid="quick-book-card">
      <div className="dt-quickbook__inner">
        <div className="dt-quickbook__head">
          <p className="dt-section__eyebrow">{t("quick_book_eyebrow")}</p>
          <h2 className="dt-quickbook__title">
            {t("quick_book_title")}
            {userName ? `, ${userName.split(" ")[0]}` : ""}
          </h2>
          <p className="dt-quickbook__desc">{t("quick_book_desc")}</p>
        </div>
        <form className="dt-quickbook__form" onSubmit={handleSubmit}>
          <label className="dt-quickbook__field">
            <span>{t("booking_field_origin")}</span>
            <input
              ref={pickupRef}
              required
              value={pickup.address}
              onChange={(e) => setPickup((s) => ({ ...s, address: e.target.value }))}
              placeholder={t("booking_placeholder_pickup")}
              autoComplete="off"
              data-testid="quick-book-pickup"
            />
          </label>
          <label className="dt-quickbook__field">
            <span>{t("booking_field_destination")}</span>
            <input
              ref={destRef}
              required
              value={dest.address}
              onChange={(e) => setDest((s) => ({ ...s, address: e.target.value }))}
              placeholder={t("booking_placeholder_destination")}
              autoComplete="off"
              data-testid="quick-book-destination"
            />
          </label>
          <label className="dt-quickbook__field">
            <span>{t("booking_field_datetime")}</span>
            <input
              required
              type="datetime-local"
              min={minDateTime}
              value={dt}
              onChange={(e) => setDt(e.target.value)}
              data-testid="quick-book-datetime"
            />
          </label>
          <label className="dt-quickbook__field">
            <span>{t("booking_field_category")}</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="quick-book-category"
            >
              {(fleet || []).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="dt-btn dt-btn--primary dt-quickbook__cta"
            data-testid="quick-book-submit"
          >
            {t("quick_book_cta")} <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </section>
  );
}
